import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import './Tabs.css';

export interface TabItem<V extends string = string> {
  readonly value: V;
  readonly label: ReactNode;
}

export interface TabsProps<V extends string = string> {
  /** Abas na ordem de exibição. Abas condicionais: monte o array já filtrado. */
  readonly tabs: ReadonlyArray<TabItem<V>>;
  /** Aba ativa (controlado). */
  readonly value: V;
  readonly onValueChange: (value: V) => void;
  /** Nome acessível do tablist (obrigatório — WCAG). */
  readonly 'aria-label': string;
  /** Prefixo dos ids (para casar com os tabpanels). Default 'tab'. Use um por
   *  grupo se houver mais de um tablist na mesma página. */
  readonly idPrefix?: string;
  readonly className?: string;
}

/** Id do BOTÃO da aba — use em `aria-labelledby` do tabpanel correspondente. */
export function tabId(value: string, idPrefix = 'tab'): string {
  return `${idPrefix}-${value}`;
}
/** Id do PAINEL da aba — use em `id` do tabpanel; o botão já aponta via aria-controls. */
export function tabPanelId(value: string, idPrefix = 'tab'): string {
  return `${idPrefix}-panel-${value}`;
}

/**
 * Tabs semântico (WAI-ARIA Tabs, ativação automática):
 *  - role="tablist"/"tab" + aria-selected + aria-controls;
 *  - roving tabindex (só a aba ativa é tabbable);
 *  - navegação por ←/→ (com wrap) + Home/End; estado ativo por sublinhado (sem glow).
 *
 * Renderiza SÓ o tablist — cada app mantém seu estado e renderiza os tabpanels.
 * No tabpanel, use `id={tabPanelId(value)}`, `aria-labelledby={tabId(value)}`,
 * `role="tabpanel"` e `tabIndex={0}`.
 */
export function Tabs<V extends string>({
  tabs,
  value,
  onValueChange,
  idPrefix = 'tab',
  className,
  ...rest
}: TabsProps<V>): JSX.Element {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = tabs.findIndex((t) => t.value === value);

  function move(next: number): void {
    const target = tabs[next];
    if (!target) return;
    onValueChange(target.value);
    refs.current[next]?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const last = tabs.length - 1;
    if (e.key === 'ArrowRight') move(activeIndex >= last ? 0 : activeIndex + 1);
    else if (e.key === 'ArrowLeft') move(activeIndex <= 0 ? last : activeIndex - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(last);
    else return;
    e.preventDefault();
  }

  return (
    <div
      role="tablist"
      aria-label={rest['aria-label']}
      className={cn('cf-tabs', className)}
      onKeyDown={onKeyDown}
    >
      {tabs.map((tab, i) => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={tabId(tab.value, idPrefix)}
            aria-selected={selected}
            aria-controls={tabPanelId(tab.value, idPrefix)}
            tabIndex={selected ? 0 : -1}
            className={cn('cf-tab', selected && 'cf-tab--active')}
            onClick={() => {
              onValueChange(tab.value);
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
